import type { CrmObject } from '@/types/crm';

import { getMeetingSeriesId } from './recurringSeries';

export const MEETING_NOTES_TO_MEETING_ASSOCIATION =
  'ahoy_meeting_notes_to_meeting';

export function getMeetingEntityId(note: CrmObject): string | null {
  const assoc = note.associations?.find(
    a => a.association_type_name === MEETING_NOTES_TO_MEETING_ASSOCIATION
  );
  return assoc?.target_entity_id ?? null;
}

export interface MeetingSummaryOccurrence {
  note: CrmObject;
  meeting: CrmObject | null;
}

export type MeetingSummaryItem =
  | { kind: 'note'; note: CrmObject; meeting: CrmObject | null }
  | {
      kind: 'series';
      seriesId: string;
      occurrences: MeetingSummaryOccurrence[];
    };

export function groupMeetingSummaries(
  notes: CrmObject[],
  associatedObjects: Record<string, CrmObject>
): MeetingSummaryItem[] {
  const resolved = notes.map(note => {
    const meetingId = getMeetingEntityId(note);
    const meeting = meetingId ? (associatedObjects[meetingId] ?? null) : null;
    const seriesId = getMeetingSeriesId(meeting);
    return { note, meeting, seriesId };
  });

  const counts = new Map<string, number>();
  for (const { seriesId } of resolved) {
    if (seriesId !== null) {
      counts.set(seriesId, (counts.get(seriesId) ?? 0) + 1);
    }
  }

  const items: MeetingSummaryItem[] = [];
  const seriesIndex = new Map<string, number>();

  for (const { note, meeting, seriesId } of resolved) {
    if (seriesId !== null && (counts.get(seriesId) ?? 0) >= 2) {
      const existingIndex = seriesIndex.get(seriesId);
      if (existingIndex === undefined) {
        seriesIndex.set(seriesId, items.length);
        items.push({
          kind: 'series',
          seriesId,
          occurrences: [{ note, meeting }],
        });
      } else {
        const seriesItem = items[existingIndex];
        if (seriesItem.kind === 'series') {
          seriesItem.occurrences.push({ note, meeting });
        }
      }
    } else {
      items.push({ kind: 'note', note, meeting });
    }
  }

  return items;
}
